import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Stack} from '@mui/material';
import type {FilterSet} from 'services/advancedSearch/types';
import {useLocalization} from 'localization';
import {
  useDeleteFilterSetMutation,
  useGetFilterSetsQuery,
} from 'services/advancedSearch/advancedSearchApi';
import {mapRtkErrorToHttpError} from 'store/api';
import {
  ActivityIndicator,
  Label,
  Text,
  useAlertDialogAsPromiseV3,
  useSnacks,
} from 'components';
import {useAdvancedSearchContext} from '../context/AdvancedSearchContext';
import {compareFilters} from '../functions/compareFilters';
import {ChevronToggle} from './ChevronToggle';
import {ListItem} from './ListItem';

type Props = {
  onStartEdit: (fs: FilterSet) => void;
  editingFilterSetId?: string | null;
};

export const FilterSets: React.FC<Props> = ({
  onStartEdit,
  editingFilterSetId,
}) => {
  const {errorSnack} = useSnacks();
  const {currentFilters, setCurrentFilters} = useAdvancedSearchContext();

  const {data: filterSets, isLoading: dataIsLoading} = useGetFilterSetsQuery();
  const [deleteFilterSet, {isLoading: deleteIsLoading, error: deletingError}] =
    useDeleteFilterSetMutation();

  useEffect(() => {
    if (deletingError) {
      errorSnack(
        'Deleting filter set failed',
        mapRtkErrorToHttpError(deletingError),
      );
    }
  }, [deletingError, errorSnack]);

  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const limit = 5;
  const {t} = useLocalization();
  const {
    pleaseConfirm: pleaseConfirmDeleteFilter,
    AlertDialog: ConfirmDeleteFilter,
    isOpen,
    isClosing,
  } = useAlertDialogAsPromiseV3();

  const handleDelete = useCallback(
    (id: string) => {
      pleaseConfirmDeleteFilter(
        () => {
          deleteFilterSet({id});
        },
        {
          title: t('general.areyousure'),
          okTitle: t('general.delete'),
          okIcon: 'Delete',
          cancelTitle: t('general.cancel'),
        },
      );
    },
    [deleteFilterSet, pleaseConfirmDeleteFilter, t],
  );

  /**
   * Filtered by private and public, then name.
   */
  const ExpandableFilterSets = useMemo(() => {
    const items: React.ReactNode[] = [];

    if (!filterSets) {
      return items;
    }

    // Headers only displayed one time each, or never if no items match
    let firstPrivate = filterSets.filter(c => !c.isPublic).length > 0;
    let firstPublic = filterSets.filter(c => c.isPublic).length > 0;

    filterSets.slice(0, isExpanded ? undefined : limit).forEach(f => {
      if (firstPrivate && !f.isPublic) {
        items.push(
          <Label key={'header-private'}>{t('data.field.private.label')}</Label>,
        );
        firstPrivate = false;
      }
      if (firstPublic && f.isPublic) {
        items.push(
          <Label key={'header-public'}>{t('data.field.public.label')}</Label>,
        );
        firstPublic = false;
      }
      items.push(
        <ListItem
          key={f.id}
          label={f.name}
          active={compareFilters(currentFilters, f.filters)}
          disabled={!!editingFilterSetId && editingFilterSetId !== f.id}
          onClick={() => setCurrentFilters(f.filters)}
          actions={[
            {
              position: 'afterText',
              props: {
                icon: 'Edit',
                size: 'small',
                onClick: () => onStartEdit(f),
                disabled: !!editingFilterSetId && editingFilterSetId !== f.id,
              },
            },
            {
              position: 'afterText',
              props: {
                icon: 'Delete',
                size: 'small',
                onClick: () => handleDelete(f.id),
                disabled: !!editingFilterSetId && editingFilterSetId !== f.id,
              },
            },
          ]}
        />,
      );
    });

    return items;
  }, [
    currentFilters,
    editingFilterSetId,
    filterSets,
    handleDelete,
    isExpanded,
    onStartEdit,
    setCurrentFilters,
    t,
  ]);

  return (
    <>
      <Text variant={'body1'}>Lagrede søk</Text>

      {dataIsLoading || deleteIsLoading ? (
        <ActivityIndicator />
      ) : (
        <Stack p={1} direction={'column'} spacing={1} overflow={'hidden'}>
          {ExpandableFilterSets}

          {(filterSets ?? []).length > limit ? (
            <ChevronToggle
              isExpanded={isExpanded}
              onClick={() => setIsExpanded(o => !o)}>
              {isExpanded ? 'Se færre' : 'Se flere'}
            </ChevronToggle>
          ) : null}
        </Stack>
      )}
      <ConfirmDeleteFilter isOpen={isOpen} isClosing={isClosing} />
    </>
  );
};
